import { useRef, useState } from 'react';

/**
 * useState but has a second "follow" parameter which is used as an automatic update on the state value if the "follow" parameter changes
 * This accomplishes the same as useState with a useEffect where the effect updates the state value when the "follow" dep changes
 * The advantage is that when using this hook there is no need for a second render loop
 */
export function useOverridableState<T>(
  initialValue: T,
  follow: T,
): [current: T, update: (newVal: T) => void] {
  const [val, setVal] = useState<T>(initialValue);

  const prevFollow = useRef<T>(follow);

  const refVal = useRef<T>(val);

  if (follow !== prevFollow.current) {
    refVal.current = follow;
  }

  prevFollow.current = follow;

  return [
    refVal.current,
    (newVal: T) => {
      refVal.current = newVal;
      setVal(newVal);
    },
  ];
}
